import { useCallback, useRef, useState } from 'react'

import useCustomSnackbar from 'Components/UI/Atoms/SnackBar'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import InputsWithValidate from 'Components/UI/Molecules/InputsWithValidate'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { CITIES, ESTADO, PROVINCIAS, TIPO_RESPONSABLE } from 'Constants'
import { Form, Formik } from 'formik'
import { interfaces } from 'service/interfaces'
import * as yup from 'yup'

import { Step1 } from './Content'

const SEARCH_NUMERIC_RE = /^\d+$/

const DEFAULT_INFO = {
  nacionalidad: 'LOCAL',
  search: '',
  nombre: '',
  tipo_responsable: null,
  estado: null,
  provincia: null,
  localidad: null,
  domicilio: '',
  telefono: '',
  mail: '',
  notas: '',
  cond_iva: null,
  cuit: undefined,
  cp: undefined,
}

const requiredSelectField = yup.mixed().transform((value, originalValue) => {
  if (typeof originalValue === 'string' && originalValue.trim() === '') {
    return null
  }
  return value
})

const validationSchema = yup.object().shape({
  nacionalidad: yup.string().required('Campo obligatorio'),
  search: yup.string().trim().matches(/^\d+$/, 'Debe ser numérico').required('Campo obligatorio'),
  nombre: yup.string().trim().required('Campo obligatorio'),
  tipo_responsable: requiredSelectField.when('nacionalidad', {
    is: (val) => val === 'LOCAL',
    then: (schema) => schema.required('Campo obligatorio'),
    otherwise: (schema) => schema,
  }),
  provincia: requiredSelectField.when('nacionalidad', {
    is: (val) => val === 'LOCAL',
    then: (schema) => schema.required('Campo obligatorio'),
    otherwise: (schema) => schema,
  }),
  localidad: requiredSelectField.when('nacionalidad', {
    is: (val) => val === 'LOCAL',
    then: (schema) => schema.required('Campo obligatorio'),
    otherwise: (schema) => schema,
  }),
  domicilio: yup.string().when('nacionalidad', {
    is: (val) => val === 'LOCAL',
    then: (schema) => schema.trim().required('Campo obligatorio'),
    otherwise: (schema) => schema,
  }),
  telefono: yup
    .string()
    .trim()
    .matches(/^\d+$/, 'Debe ser numérico')
    .when('nacionalidad', {
      is: (val) => val === 'LOCAL' || val === 'EXTRANJERO',
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema,
    }),
  mail: yup.string().trim().email('Formato inválido'),
  notas: yup.string().when('nacionalidad', {
    is: (val) => val === 'EXTRANJERO',
    then: (schema) => schema.trim().required('Campo obligatorio'),
    otherwise: (schema) => schema,
  }),
})

const toNumberOrNull = (val) => {
  if (val === null || val === undefined) return null
  const num = Number(typeof val === 'string' ? val.trim() : val)
  return Number.isNaN(num) ? val : num
}

const trimIfString = (val) => (typeof val === 'string' ? val.trim() : val)

const normalizeText = (val) => {
  if (val === null || val === undefined) return ''
  return String(val)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
}

const canonicalProvincia = (provincia) => {
  if (provincia === null || provincia === undefined) return provincia
  const match = PROVINCIAS.find((p) => normalizeText(p.provincia) === normalizeText(provincia))
  return match ? match.provincia : trimIfString(provincia)
}

const canonicalLocalidad = (localidad, provincia) => {
  if (localidad === null || localidad === undefined) return localidad
  const provinciaId = PROVINCIAS.find(
    (p) => normalizeText(p.provincia) === normalizeText(provincia)
  )?.id
  const pool = provinciaId ? CITIES.filter((c) => c.provincia_id === provinciaId) : CITIES
  const match = pool.find((c) => normalizeText(c.localidad) === normalizeText(localidad))
  return match ? match.localidad : trimIfString(localidad)
}

const toStringOrUndefined = (val) => (val === null || val === undefined ? undefined : String(val))

const toNumberOrUndefined = (val) => {
  if (val === null || val === undefined) return undefined
  const num = Number(val)
  return Number.isNaN(num) ? undefined : num
}

const mapClientToFormValues = (clientData, prevValues) => {
  const rawTipoResponsable = clientData?.tipo_responsable
  const normalizedTipoResponsable = (() => {
    const asNumber = toNumberOrNull(rawTipoResponsable)
    if (TIPO_RESPONSABLE.some((t) => t.value === asNumber)) return asNumber

    const matchByName = TIPO_RESPONSABLE.find(
      (t) => normalizeText(t.name) === normalizeText(rawTipoResponsable)
    )
    return matchByName ? matchByName.value : null
  })()

  const rawEstado = clientData?.estado
  const normalizedEstado = (() => {
    const asNumber = toNumberOrNull(rawEstado)
    if (ESTADO.some((e) => e.value === asNumber)) return asNumber

    const matchByLabel = ESTADO.find((e) => normalizeText(e.label) === normalizeText(rawEstado))
    return matchByLabel ? matchByLabel.value : null
  })()

  const provincia = canonicalProvincia(clientData?.provincia)

  return {
    ...prevValues,
    tipo_responsable: normalizedTipoResponsable,
    nombre: clientData?.nombre,
    estado: normalizedEstado,
    provincia,
    localidad: canonicalLocalidad(clientData?.localidad, provincia),
    domicilio: trimIfString(clientData?.domicilio),
    telefono: clientData?.telefono ?? '',
    mail: clientData?.mail ?? '',
    notas: clientData?.notas ?? '',
    cond_iva: clientData?.cond_iva,
    cuit: toStringOrUndefined(clientData?.cuit),
    cp: toNumberOrUndefined(clientData?.cp),
  }
}

const Create = (props) => {
  const { open, handleClose, render, setRender } = props

  const createDismissableSnackbar = useCustomSnackbar()

  const formikRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)

  const [dbs, setDbs] = useState()

  const [initialValues, setInitialValues] = useState(DEFAULT_INFO)
  const clearData = useCallback(() => {
    setDbs(undefined)
    setInitialValues(DEFAULT_INFO)
  }, [])

  const handleSubmitCode = useCallback(async () => {
    const formik = formikRef.current
    const values = formik?.values

    if (!values) return

    if (!SEARCH_NUMERIC_RE.test(String(values.search || '').trim())) {
      formik.setTouched({ search: true }, true)
      return
    }

    const search = {
      search: values.search,
      nacionalidad: values.nacionalidad,
    }
    setIsLoading(true)
    try {
      const res = await interfaces.salesClients.searchClients(search)

      if (res?.data?.header?.error) {
        const message = res?.data?.data
        setDbs(undefined)
        createDismissableSnackbar(message, {
          variant: 'error',
        })
        return
      }

      const clientData = res?.data?.cliente
      setDbs(res?.data?.dbs)
      setInitialValues(mapClientToFormValues(clientData, values))
    } catch (error) {
      const message =
        error?.response?.data?.data || error?.response?.data?.message || error?.response?.data

      createDismissableSnackbar(message, {
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [createDismissableSnackbar])

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const selectedDbs = Array.isArray(dbs) ? dbs.filter((db) => db !== 'ARCA') : []
        const dbsToCreate = selectedDbs.length > 0 ? selectedDbs : ['ECO']

        const isExtranjero = values.nacionalidad === 'EXTRANJERO'
        const condIva = isExtranjero ? 91 : (values.cond_iva ?? values.tipo_responsable)
        const tipoResponsable = isExtranjero
          ? (values.tipo_responsable ?? 5)
          : values.tipo_responsable

        const payload = {
          dbs: dbsToCreate,
          nombre: values.nombre,
          domicilio: values.domicilio,
          localidad: values.localidad,
          cp: values.cp,
          provincia: values.provincia,
          cond_iva: condIva,
          cuit: values.cuit || values.search,
          telefono1: values.telefono,
          mail1: values.mail,
          notas: values.notas,
          //tipo: 1,
          tipo_responsable: tipoResponsable,
        }
        setIsLoading(true)
        await interfaces.salesClients
          .create(payload)
          .then((res) => {
            createDismissableSnackbar(res.data, {
              variant: 'success',
            })
            handleClose()
            setRender(!render)
          })
          .catch((error) => {
            const message =
              error?.response?.data?.data ||
              error?.response?.data?.message ||
              error?.response?.data ||
              'Error'

            createDismissableSnackbar(message, {
              variant: 'error',
            })
          })
        handleClose()
        setIsLoading(false)
      }}
    >
      {({ values, setFieldValue, setTouched, isSubmitting, submitForm }) => {
        return (
          <DialogComponent
            title="Nuevo cliente"
            open={open}
            handleclose={handleClose}
            handleclosedisabled={isLoading}
            widthLarge="800px"
            disableBackdropClick
            disableEscapeKeyDown
            maxHeight
            children={
              <Form>
                <div>
                  {dbs ? (
                    <div
                      style={{
                        color: '#266f51',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        backgroundColor: '#5ebf6d63',
                        borderRadius: '.5rem',
                        padding: '.4rem',
                        marginBottom: '15px',
                      }}
                    >
                      {`Origenes existentes: ${dbs.join(', ')}`}
                    </div>
                  ) : null}

                  <InputsWithValidate
                    fields={Step1(values, handleSubmitCode, setFieldValue, isLoading, Boolean(dbs))}
                    spacing={2}
                    setFieldValue={setFieldValue}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </Form>
            }
            actions={
              <CancelConfirmButtons
                leftButton
                leftButtonText="Limpiar datos"
                leftButtonOnClick={clearData}
                leftButtonDisabled={isLoading}
                leftButtonColor="default"
                leftButtonHiddenFalse
                leftButtonStyle={{
                  width: '20%',
                }}
                disabledSubmit={isLoading}
                disabledCancel={isLoading}
                isLoading={isLoading}
                type="button"
                onClickSubmit={() => {
                  setTouched(
                    {
                      nacionalidad: true,
                      search: true,
                      nombre: true,
                      tipo_responsable: true,
                      provincia: true,
                      localidad: true,
                      domicilio: true,
                      telefono: true,
                      mail: true,
                      notas: true,
                    },
                    true
                  )
                  submitForm()
                }}
                onClickCancel={handleClose}
              />
            }
          />
        )
      }}
    </Formik>
  )
}
export default Create
